import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { Checkbox } from '../../components/ui/Checkbox';
import Icon from '../../components/AppIcon';


const Login = () => {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [generalError, setGeneralError] = useState('');

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.email) {
      newErrors.email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    if (generalError) {
      setGeneralError('');
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setGeneralError('');

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    const { data, error } = await signIn(formData?.email, formData?.password);
    
    if (error) {
      setGeneralError(error?.message || 'Failed to sign in. Please check your credentials.');
      setIsLoading(false);
    } else if (data?.user) {
      navigate('/meal-planning');
    }
  };

  const handleForgotPassword = () => {
    console.log('Navigate to password recovery');
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        <div className="text-center mb-8 md:mb-10 lg:mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 lg:w-24 lg:h-24 rounded-2xl mb-4 md:mb-5 lg:mb-6" style={{ background: 'linear-gradient(135deg, var(--color-primary) 0%, var(--color-secondary) 100%)' }}>
            <img src="/assets/images/B42A68D7-7405-4964-872F-D36C4B1CBA1C-1768359280145.jpg" alt="GalleyPlan Logo" className="w-full h-full object-cover rounded-2xl" />
          </div>
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-2 md:mb-3">
            Welcome to GalleyPlan
          </h1>
          <p className="text-sm md:text-base lg:text-lg text-muted-foreground">
            Sign in to manage your coastal cruise provisions
          </p>
        </div>

        <div className="bg-card rounded-lg md:rounded-xl lg:rounded-2xl shadow-elevation-2 p-6 md:p-8 lg:p-10">
          {generalError && (
            <div className="mb-4 md:mb-5 lg:mb-6 p-3 md:p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-3">
              <Icon name="AlertCircle" size={20} color="var(--color-destructive)" className="flex-shrink-0 mt-0.5" />
              <p className="text-sm md:text-base text-destructive">{generalError}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4 md:space-y-5 lg:space-y-6">
            <Input
              label="Email Address"
              type="email"
              name="email"
              placeholder="captain@example.com"
              value={formData?.email}
              onChange={handleInputChange}
              error={errors?.email}
              required
              disabled={isLoading}
            />

            <div className="relative">
              <Input
                label="Password"
                type={showPassword ? 'text' : 'password'}
                name="password"
                placeholder="Enter your password"
                value={formData?.password}
                onChange={handleInputChange}
                error={errors?.password}
                required
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-colors"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                disabled={isLoading}
              >
                <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
              </button>
            </div>

            <div className="flex items-center justify-between">
              <Checkbox
                label="Remember me"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e?.target?.checked)}
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={handleForgotPassword}
                className="text-sm md:text-base text-primary hover:text-primary/80 font-medium transition-colors"
                disabled={isLoading}
              >
                Forgot password?
              </button>
            </div>

            <Button
              type="submit"
              variant="default"
              size="lg"
              fullWidth
              loading={isLoading}
              disabled={isLoading}
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <div className="mt-6 md:mt-7 lg:mt-8 pt-6 md:pt-7 lg:pt-8 border-t border-border">
            <p className="text-center text-sm md:text-base text-muted-foreground">
              Don't have an account?{' '}
              <button
                onClick={() => navigate('/register')}
                className="text-primary hover:text-primary/80 font-medium transition-colors"
                disabled={isLoading}
              >
                Create Account
              </button>
            </p>
          </div>
        </div>

        <div className="mt-6 md:mt-7 lg:mt-8 text-center">
          <div className="flex items-center justify-center gap-2 text-xs md:text-sm text-muted-foreground">
            <Icon name="Shield" size={16} />
            <span>Secure SSL encrypted connection</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;